/**
 * RefundPolicyPage.jsx
 * Public page — no auth required.
 * Route: /vocab/refund-policy
 */
import { Mascot } from "@/components/styleguide/Mascot";

export default function RefundPolicyPage() {
  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Quicksand:wght@500;600;700&display=swap"
        rel="stylesheet"
      />

      <div
        style={{
          minHeight: "100vh",
          background: "#FFF7F0",
          color: "#262A44",
          fontFamily: "'Quicksand', system-ui, sans-serif",
          fontWeight: 500,
          WebkitFontSmoothing: "antialiased",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header
          style={{
            borderBottom: "1px solid #F0E4D8",
            background: "#FFF7F0",
          }}
        >
          <div
            style={{
              maxWidth: 860,
              margin: "0 auto",
              padding: "16px clamp(16px, 5vw, 40px)",
            }}
          >
            <a
              href="/vocab"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px 6px 8px",
                background: "#FFFFFF",
                borderRadius: 999,
                boxShadow: "0 4px 18px rgba(194,65,12,.08)",
                color: "#262A44",
                textDecoration: "none",
              }}
            >
              <Mascot size={28} animated={false} />
              <span
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 900,
                  fontSize: 16,
                  letterSpacing: "-.01em",
                }}
              >
                Bloom Vocabulary
              </span>
            </a>
          </div>
        </header>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <main style={{ flex: 1 }}>
          <div
            style={{
              maxWidth: 720,
              margin: "0 auto",
              padding:
                "clamp(40px, 7vw, 80px) clamp(20px, 5vw, 48px) clamp(56px, 8vw, 96px)",
              display: "flex",
              flexDirection: "column",
              gap: 32,
            }}
          >
            {/* Title block */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span
                style={{
                  font: "700 12px 'Quicksand', sans-serif",
                  letterSpacing: ".14em",
                  color: "#C2410C",
                  textTransform: "uppercase",
                }}
              >
                Legal
              </span>
              <h1
                style={{
                  margin: 0,
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 900,
                  fontSize: "clamp(32px, 6vw, 48px)",
                  lineHeight: 1.05,
                  letterSpacing: "-.03em",
                }}
              >
                Refund Policy
              </h1>
            </div>

            {/* Policy card */}
            <div
              style={{
                padding: "clamp(24px, 4vw, 40px)",
                borderRadius: 24,
                background: "#FFFFFF",
                border: "1px solid #F0E4D8",
                boxShadow: "0 8px 32px rgba(38,42,68,.07)",
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              <PolicyPara>
                If you're not happy with Bloom, you can request a refund within{" "}
                <strong>14 days</strong> of your first subscription payment by
                emailing{" "}
                <a
                  href="mailto:team@webuilthere.com"
                  style={{ color: "#C2410C", fontWeight: 700 }}
                >
                  team@webuilthere.com
                </a>{" "}
                with your Bloom account email address.
              </PolicyPara>

              <PolicyPara>
                We'll review your request and respond as soon as possible.
              </PolicyPara>

              <PolicyPara>
                After the 14-day period, refunds are not normally offered for
                the remaining part of a billing period, except where required by
                law.
              </PolicyPara>

              <PolicyPara>
                This policy does not affect your statutory consumer rights,
                including any cancellation or refund rights that may apply under
                UK law.
              </PolicyPara>

              <PolicyPara>
                For subscription cancellations, please contact us at{" "}
                <a
                  href="mailto:team@webuilthere.com"
                  style={{ color: "#C2410C", fontWeight: 700 }}
                >
                  team@webuilthere.com
                </a>{" "}
                or use the cancellation option available in your account.
              </PolicyPara>
            </div>

            {/* Back link */}
            <a
              href="/vocab"
              style={{
                alignSelf: "flex-start",
                font: "700 15px 'Quicksand', sans-serif",
                color: "#C2410C",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              ← Back to Bloom
            </a>
          </div>
        </main>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <footer style={{ borderTop: "1px solid #F0E4D8" }}>
          <div
            style={{
              maxWidth: 860,
              margin: "0 auto",
              padding: "20px clamp(20px, 5vw, 48px) 28px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px 24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Mascot size={22} animated={false} />
              <span style={{ font: "800 14px 'Nunito', sans-serif" }}>
                Bloom Vocabulary
              </span>
              <span
                style={{
                  font: "500 13px 'Quicksand', sans-serif",
                  color: "#6E7289",
                }}
              >
                © 2026
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px 20px",
                font: "600 13px 'Quicksand', sans-serif",
              }}
            >
              {[
                { label: "Privacy", href: "/vocab/privacy" },
                { label: "Terms", href: "/vocab/terms" },
                { label: "Refund Policy", href: "/vocab/refund-policy" },
              ].map(({ label, href }) => (
                <a key={label} href={href} style={{ color: "#545872" }}>
                  {label}
                </a>
              ))}
              <span style={{ color: "#6E7289" }}>
                Built by{" "}
                <a
                  href="https://webuilthere.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#C2410C", fontWeight: 700 }}
                >
                  We Built Here
                </a>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function PolicyPara({ children }) {
  return (
    <p
      style={{
        margin: 0,
        font: "500 16px/1.65 'Quicksand', sans-serif",
        color: "#3E4260",
        textWrap: "pretty",
      }}
    >
      {children}
    </p>
  );
}
